import React from 'react';
import { Box, Container, HStack, Image, Link, Skeleton, SkeletonText, Stack, Text, VStack, Icon } from '@chakra-ui/react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { getDonationFormBySlug, getDonationReceipt, logger, type DonationForm as DonationFormModel } from '@kuow/core';
import { UpdateBillingDonationForm } from '@kuow/shared-ui';

import niceCheckmark from '../assets/nice-checkmark.svg';
import { getAssetUrl, getImpactContent } from '../utils/donationFormTheme';
import { LuMail, LuPhone } from 'react-icons/lu';

const BILLING_UPDATE_FORM_SLUG = 'updateinfo';
const BILLING_FALLBACK_PATH = '/donate';

export const UpdateBillingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const receiptNumber = (searchParams.get('number') ?? '').trim();
  const updateToken = (searchParams.get('token') ?? '').trim();
  const [isLoadingDetails, setIsLoadingDetails] = React.useState(false);
  const [shouldRedirect, setShouldRedirect] = React.useState(false);
  const [formDetails, setFormDetails] = React.useState<DonationFormModel | null>(null);
  const [memberFirstName, setMemberFirstName] = React.useState('');

  React.useEffect(() => {
    if (!receiptNumber || !updateToken) return;

    let isMounted = true;
    const controller = new AbortController();
    setIsLoadingDetails(true);
    setShouldRedirect(false);
    setFormDetails(null);
    setMemberFirstName('');

    const load = async () => {
      try {
        const [details, receipt] = await Promise.all([
          getDonationFormBySlug(BILLING_UPDATE_FORM_SLUG, { signal: controller.signal }),
          getDonationReceipt(receiptNumber, { updateToken, signal: controller.signal }),
        ]);
        if (!isMounted) return;
        setFormDetails(details);
        setMemberFirstName(receipt.member?.first_name ?? '');
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        logger.error('Failed to load update billing form details for UpdateBillingPage; redirecting to fallback', {
          formSlug: BILLING_UPDATE_FORM_SLUG,
          error: err,
        });
        if (!isMounted) return;
        setShouldRedirect(true);
        setFormDetails(null);
        setMemberFirstName('');
      } finally {
        if (isMounted) {
          setIsLoadingDetails(false);
        }
      }
    };

    void load();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [receiptNumber, updateToken]);

  if (!receiptNumber || !updateToken || shouldRedirect) {
    return <Navigate to={BILLING_FALLBACK_PATH} replace />;
  }

  const backgroundImageUrl = getAssetUrl(formDetails?.background_image);
  const themeImageUrl = getAssetUrl(formDetails?.theme_image);
  const { impactHeadline, impactBullets, hasImpactSection } = getImpactContent(formDetails);
  const welcomeHeadline = memberFirstName ? `Welcome back ${memberFirstName}` : 'Welcome Back';

  return (
    <Box
      w="full"
      flex="1"
      display="flex"
      flexDirection="column"
      position="relative"
      backgroundImage={backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined}
      backgroundRepeat="no-repeat"
      backgroundPosition="center"
      backgroundSize="cover"
      _before={
        backgroundImageUrl
          ? {
              content: '""',
              position: 'absolute',
              inset: 0,
              bg: 'blackAlpha.600',
            }
          : undefined
      }
    >
      <Box flex="1" display="flex" flexDirection="column" justifyContent={{ base: 'flex-start', md: 'center' }} py={{ base: 10, md: 10 }}>
        <Container maxW="7xl" px={{ base: 6, md: 12 }} position="relative" zIndex={1}>
          <Stack
            direction={{ base: 'column-reverse', md: 'row' }}
            align={{ base: 'center', md: 'flex-start' }}
            justify="center"
            gap={{ base: 10, md: 12 }}
          >
            <Box flex="1" color="fg.inverted" minW="0" maxW="lg">
              <VStack align="start" gap={{ base: 6, lg: 8 }} pt={{ base: 0, md: 12 }} pb={{ base: 10, md: 12 }}>
                <>
                  {isLoadingDetails ? (
                    <Box w="full">
                      <Skeleton height="44px" borderRadius="md" />
                    </Box>
                  ) : themeImageUrl ? (
                    <Stack w="full" gap={{ base: 6, lg: 8 }} direction={{ base: 'column', lg: 'row' }} align={{ base: 'center', lg: 'end' }}>
                      <Box w="full" maxW={{ base: '56', lg: '64' }} aspectRatio="1" flexShrink={0}>
                        <Image src={themeImageUrl} alt={formDetails?.theme_image?.title ?? ''} w="full" h="full" objectFit="cover" />
                      </Box>
                      <Text as="h1" fontSize="4xl" fontWeight="semibold">
                        {welcomeHeadline}
                      </Text>
                    </Stack>
                  ) : (
                    <Text as="h1" fontSize="4xl" fontWeight="semibold">
                      {welcomeHeadline}
                    </Text>
                  )}

                  {isLoadingDetails ? (
                    <SkeletonText noOfLines={4} gap={3} w="full" />
                  ) : (
                    <>
                      <Text opacity={0.9}>Not you? Email or call our membership team:</Text>
                      <VStack gap={1} align="start" mt={4}>
                        <Box><Icon as={LuMail} mr={1} /> <Link href="mailto:membership@kuow.org" color="fg.inverted">membership@kuow.org</Link></Box>
                        <Box><Icon as={LuPhone} mr={1} /> <Link href="tel:+12062212501" color="fg.inverted">(206) 221-2501</Link></Box>
                      </VStack>
                      <Text fontSize="lg" opacity={0.9}>
                        {formDetails?.description ?? ''}
                      </Text>
                    </>
                  )}

                  {!isLoadingDetails && hasImpactSection && (
                    <VStack gap={2.5} w="full" align="stretch" pt={2}>
                      {impactHeadline && (
                        <Text fontWeight="semibold" fontSize="md" textAlign="center">
                          {impactHeadline}
                        </Text>
                      )}

                      {impactBullets.map((bullet, idx) => (
                        <HStack key={`${idx}:${bullet}`} gap={2.5} align="center">
                          <Image src={String(niceCheckmark)} alt="" boxSize={6.5} />
                          <Text fontSize="sm" opacity={0.9} dangerouslySetInnerHTML={{ __html: bullet }}></Text>
                        </HStack>
                      ))}
                    </VStack>
                  )}
                </>
              </VStack>
            </Box>

            <Box flex="1" w={{ base: 'full', md: 'auto' }} minW="300px" maxW="lg">
              <Box w="full" bg="bg" borderRadius="l3" px={8} pt={4} pb={8}>
                <UpdateBillingDonationForm receiptNumber={receiptNumber} updateToken={updateToken} utmCampaign={null} />
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};


